import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

const ACCELERATION_STEP: number = 0.05;
const MIN_DISPLACEMENT: number = 0.01;
const MAX_DISPLACEMENT: number = 1;
const MIN_VELOCITY: number = -1;
const MAX_VELOCITY: number = 1;

/**
 * The `StringGlide` class is a module that handles the glide effect for string objects
 * based on scroll events. It calculates displacement, acceleration, and velocity
 * to create a smooth scrolling effect for objects.
 */
export class StringGlide extends StringModule {
  private previousLerp: number = 0;
  private displacement: number = 0;
  private acceleration: number = 0;
  private velocityMultiplier: number = 0.00125;
  private isInitialScroll: boolean = true;

  private baseVelocityMultiplier: number = 0.00125;
  private reducedVelocityMultiplier: number = this.baseVelocityMultiplier / 20;
  private negativeVelocityMultiplier: number = -0.0001;

  private maxDisplacementValue: number = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "glide";

    this.baseVelocityMultiplier =
      this.settings["glide-base-velocity"] ?? this.baseVelocityMultiplier;
    this.reducedVelocityMultiplier =
      this.settings["glide-reduce-velocity"] ?? this.reducedVelocityMultiplier;
    this.negativeVelocityMultiplier =
      this.settings["glide-negative-velocity"] ?? this.negativeVelocityMultiplier;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "glide", type: "number", fallback: this.settings["glide"] },
    ];
  }

  private setupItem = (object: StringObject) => {
    let glide = object.getProperty<number>("glide") ?? 0;

    let glideValue = -this.data.scroll.displacement * this.maxDisplacementValue * glide;
    this.events.emit(`object:glide:${object.id}`, glideValue);

    const transformCompute = `translate3d(0, ${glideValue}px, 0)`;
    object.htmlElement.style.transform = transformCompute;
  };

  private onUpdateDesktopEvent = () => {
    for (let i = 0; i < this.objects.length; i++) {
      let object = this.objects[i];
      this.setupItem(object);
    }
  };
  private onUpdateMobileEvent = () => {};
  private onUpdateEvent = this.onUpdateDesktopEvent;

  private calcExpanderFactor(isDirectionUp: boolean): void {
    const isConditionMet = isDirectionUp
      ? this.data.scroll.lerped < this.previousLerp
      : this.data.scroll.lerped > this.previousLerp;

    this.velocityMultiplier = isConditionMet
      ? this.isInitialScroll
        ? this.baseVelocityMultiplier
        : this.reducedVelocityMultiplier
      : this.negativeVelocityMultiplier;

    if (!isConditionMet) {
      this.isInitialScroll = false;
    }
  }
  onStart(): void {
    this.maxDisplacementValue = this.data.viewport.windowHeight * 0.1;
  }

  onResize(): void {
    if (window.innerWidth > 1024) {
      this.maxDisplacementValue = this.data.viewport.windowHeight * 0.1;
      this.onUpdateEvent = this.onUpdateDesktopEvent;
    } else {
      this.onUpdateEvent = this.onUpdateMobileEvent;
      this.resetState();
      this.objects.forEach((object) => {
        this.setupItem(object);
      });
    }
  }

  private resetState(): void {
    this.displacement = 0;
    this.acceleration = 0;
    this.isInitialScroll = true;
    this.velocityMultiplier = this.baseVelocityMultiplier;
  }

  onScrollStart(): void {
    this.resetState();
  }

  onScrollStop(): void {
    this.resetState();
    this.previousLerp = 0;
    //document.documentElement.style.setProperty('--glide', '0');
    for (let i = 0; i < this.objects.length; i++) {
      let object = this.objects[i];
      const transformCompute = `translate3d(0, 0px, 0)`;
      object.htmlElement.style.transform = transformCompute;
      object.htmlElement.style.setProperty("--glide", this.data.scroll.displacement.toString());
    }
  }

  onFrame(data: StringData): void {
    this.calcExpanderFactor(this.data.scroll.isScrollingDown === false);
    this.acceleration = Math.min(MAX_DISPLACEMENT, this.acceleration + ACCELERATION_STEP);
    this.displacement = Math.max(
      MIN_DISPLACEMENT,
      Math.min(MAX_DISPLACEMENT, this.displacement + this.velocityMultiplier)
    );
    this.data.scroll.displacement = Math.min(
      MAX_VELOCITY,
      Math.max(MIN_VELOCITY, this.data.scroll.lerped * this.displacement * this.acceleration)
    );
    this.objects.forEach((object) => {
      this.applyToElementAndConnects(object, (el) => {
        el.style.setProperty("--glide", this.data.scroll.displacement.toString());
      });
    });
    this.previousLerp = this.data.scroll.lerped;
    this.onUpdateEvent();
  }
}
